/* eslint-disable react/prop-types */
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react/no-children-prop */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/display-name */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useDispatch, useSelector } from 'react-redux';
import SociollaText from '@components/sociolla/SociollaText';
import ModalImage from '@components/modal/ModalImage';
import { Spacer, ButtonSticky } from '@components/screen/Layout';
import Colors from '@global/colors';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import { isAndroid } from '@global/screens';
import { logEvent } from '@global/analytics';
import CompetitorAttachmentCard from './competitor-update/CompetitorAttachmentCard';

const MAX_ATTACHMENTS = 10;

const createAttachmentId = () =>
  `competitor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptyAttachment = () => ({
  id: createAttachmentId(),
  text: '',
  url: '',
  uri: '',
});

const normalizeAttachment = (raw = {}) => ({
  id: createAttachmentId(),
  text: raw?.text || '',
  url: raw?.url || raw?.uri || '',
  uri: raw?.uri || raw?.url || '',
  ...(raw?._localFile ? { _localFile: raw._localFile } : {}),
});

const buildPayload = (attachments = []) => {
  const active = attachments
    .filter((item) => (item?.url || '').trim() || (item?.text || '').trim())
    .map((item) => ({
      text: item?.text || '',
      url: item?.url || item?.uri || '',
      ...(item?._localFile ? { _localFile: item._localFile } : {}),
    }));

  return {
    more_info: {
      attachments: [
        {
          type: 'competitor',
          name: 'competitor_update',
          image: active,
        },
      ],
    },
  };
};

const getDraftImages = (localPayload = {}, detailTodo = {}) => {
  const localAttachments =
    localPayload?.more_info?.attachments || localPayload?.attachments || [];
  const fromLocal = localAttachments?.length
    ? localAttachments.find((a) => a.type === 'competitor')
    : null;
  if (fromLocal?.image?.length) {
    return fromLocal.image;
  }
  const serverAttachments = detailTodo?.more_info?.attachments || [];
  const fromServer = serverAttachments?.length
    ? serverAttachments.find((a) => a.type === 'competitor')
    : null;
  if (fromServer?.image?.length) {
    return fromServer.image;
  }
  return [];
};

const TodoCompetitorUpdate = ({
  containerHeight = 0,
  detailData = {},
  detailTodo = {},
  localPayload = null,
  onSubmit = () => {},
  onSaveDraft = () => {},
  resetTab = () => {},
}) => {
  const dispatch = useDispatch();
  const isLoadingGlobal = useSelector((state) => state.modal.isLoading);
  const [attachments, setAttachments] = useState([createEmptyAttachment()]);
  const [uploadingMap, setUploadingMap] = useState({});
  const userEditedRef = React.useRef(false);
  const hydratedSignatureRef = React.useRef('');
  const currentTaskIdRef = React.useRef(detailTodo?._id || '');

  const updateAttachment = useCallback((id, patch = {}) => {
    userEditedRef.current = true;
    setAttachments((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  }, []);

  const removeAttachment = useCallback((id) => {
    userEditedRef.current = true;
    setAttachments((prev) => {
      const next = prev.filter((item) => item.id !== id);
      return next.length ? next : [createEmptyAttachment()];
    });
    setUploadingMap((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const setUploadingFor = useCallback((id, loading) => {
    setUploadingMap((prev) => {
      if (loading) {
        return { ...prev, [id]: true };
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const addMoreAttachment = useCallback(() => {
    userEditedRef.current = true;
    setAttachments((prev) => {
      if (prev.length >= MAX_ATTACHMENTS) {
        return prev;
      }
      return [...prev, createEmptyAttachment()];
    });
  }, []);

  const activeAttachments = useMemo(
    () =>
      attachments.filter(
        (item) => (item?.url || '').trim() || (item?.text || '').trim(),
      ),
    [attachments],
  );

  const invalidAttachments = useMemo(
    () =>
      activeAttachments.filter(
        (item) => !(item?.url || '').trim() || !(item?.text || '').trim(),
      ),
    [activeAttachments],
  );

  const isReady = useMemo(
    () => Object.keys(uploadingMap).length === 0,
    [uploadingMap],
  );

  const canSubmit = useMemo(
    () =>
      isReady &&
      !isLoadingGlobal &&
      activeAttachments.length > 0 &&
      invalidAttachments.length === 0,
    [
      isReady,
      isLoadingGlobal,
      activeAttachments.length,
      invalidAttachments.length,
    ],
  );

  const onSubmitCompetitorUpdate = async () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      if (!activeAttachments.length) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        dispatch(
          showAlert({
            show: true,
            isModal: true,
            title: 'Error',
            description: 'Please add at least one competitor photo.',
            isConfirm: false,
            testID: 'todo-competitor-update-validation',
          }),
        );
        return;
      }

      if (invalidAttachments.length) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        dispatch(
          showAlert({
            show: true,
            isModal: true,
            title: 'Error',
            description: 'Each photo must have its own description.',
            isConfirm: false,
            testID: 'todo-competitor-update-validation',
          }),
        );
        return;
      }

      const payload = buildPayload(attachments);

      logEvent({
        eventName: 'submit_activity',
        data: {
          activity_id: detailData?._id,
          task_id: detailTodo?._id,
          type: 'competitor_update',
          payload: JSON.stringify(payload),
        },
        dataKey: 'detail',
      });

      await onSubmit({
        targetType: 'competitor_update',
        taskId: detailTodo?._id,
        payload,
      });
      dispatch(showPopup({ name: 'isLoading', value: false }));
      resetTab();
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      let error_message = 'A system error occurred';
      if (error?.response?.data?.message) {
        error_message = error.response.data.message;
      }
      if (!detailTodo?._id) {
        error_message = 'This activity have no todo list activity.';
      }
      dispatch(
        showAlert({
          show: true,
          isModal: true,
          title: 'Error',
          description: error_message,
          isConfirm: false,
          testID: 'todo-competitor-update-error',
        }),
      );
    }
  };

  useEffect(() => {
    const taskId = detailTodo?._id || '';
    if (currentTaskIdRef.current !== taskId) {
      currentTaskIdRef.current = taskId;
      userEditedRef.current = false;
      hydratedSignatureRef.current = '';
      setUploadingMap({});
    }

    if (userEditedRef.current) {
      return;
    }

    const draftImages = getDraftImages(localPayload, detailTodo);
    const signature = JSON.stringify(
      draftImages.map((item) => ({
        text: item?.text || '',
        url: item?.url || item?.uri || '',
      })),
    );

    if (signature === hydratedSignatureRef.current) {
      return;
    }

    hydratedSignatureRef.current = signature;
    if (draftImages.length) {
      setAttachments(draftImages.map((item) => normalizeAttachment(item)));
    } else {
      setAttachments([createEmptyAttachment()]);
    }
  }, [detailTodo?._id, detailTodo?.more_info?.attachments, localPayload]);

  const saveDraftRef = React.useRef(onSaveDraft);

  useEffect(() => {
    saveDraftRef.current = onSaveDraft;
  }, [onSaveDraft]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const payload = buildPayload(attachments);
      saveDraftRef.current({
        targetType: 'competitor_update',
        taskId: detailTodo?._id,
        payload,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [attachments, detailTodo?._id]);

  return (
    <KeyboardAwareScrollView
      testID="todo-competitor-update-container"
      style={styles.container}
      contentContainerStyle={styles.content}
      enableOnAndroid={true}
      extraScrollHeight={80}>
      <View style={styles.introCard}>
        <SociollaText type="bold" style={styles.introTitle}>
          Competitor Evidence
        </SociollaText>
        <SociollaText style={styles.titleDesc}>
          Upload picture and description (max. 500 characters).
        </SociollaText>
        <View style={styles.introMetaRow}>
          <View style={styles.metaPill}>
            <SociollaText style={styles.metaPillText}>
              {attachments.length}/{MAX_ATTACHMENTS} section
            </SociollaText>
          </View>
          <View style={styles.metaPill}>
            <SociollaText style={styles.metaPillText}>
              1 photo = 1 note
            </SociollaText>
          </View>
        </View>
      </View>

      <View style={styles.formContainer}>
        {attachments.map((item, idx) => (
          <CompetitorAttachmentCard
            key={item.id}
            item={item}
            index={idx}
            canRemove={
              attachments.length > 1 || Boolean(item?.url || item?.text)
            }
            detailData={detailData}
            detailTodo={detailTodo}
            loading={Boolean(uploadingMap[item.id])}
            onUploading={(loading) => setUploadingFor(item.id, loading)}
            onRemove={() => removeAttachment(item.id)}
            onImageUploaded={(uploaded) => {
              const uri = uploaded?.uri || uploaded?.url || '';
              updateAttachment(item.id, {
                url: uri,
                uri,
                _localFile:
                  uri && !uri.startsWith('http')
                    ? {
                        uri,
                        filename: uploaded?.filename,
                        type: uploaded?.type,
                        mimeType: uploaded?.mimeType,
                      }
                    : undefined,
              });
            }}
            onDescriptionChange={(text) => updateAttachment(item.id, { text })}
          />
        ))}

        {attachments.length < MAX_ATTACHMENTS ? (
          <TouchableOpacity
            testID="todo-competitor-update-add-more-btn"
            activeOpacity={0.7}
            style={styles.addMoreButton}
            onPress={addMoreAttachment}>
            <SociollaText type="bold" style={styles.addMoreText}>
              + Add more photo
            </SociollaText>
            <SociollaText style={styles.addMoreSubtext}>
              Add another photo with its own description
            </SociollaText>
          </TouchableOpacity>
        ) : null}
      </View>

      {!isAndroid ? <Spacer /> : null}
      <ModalImage />
      <ButtonSticky
        show
        customHost="modal-sticky-button"
        buttons={[
          {
            title: !isReady ? 'Uploading...' : 'Submit Competitor Update',
            disabled: !canSubmit,
            onPress: onSubmitCompetitorUpdate,
          },
        ]}
      />
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.grey16,
  },
  content: {
    paddingBottom: 24,
  },
  introCard: {
    marginHorizontal: 15,
    marginTop: 12,
    marginBottom: 12,
    borderRadius: 12,
    padding: 14,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.grey0,
  },
  introTitle: {
    fontSize: 16,
    color: Colors.black,
    marginBottom: 6,
  },
  titleDesc: {
    color: Colors.grey2,
    lineHeight: 20,
    fontSize: 13,
  },
  introMetaRow: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  metaPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: Colors.grey0,
    backgroundColor: Colors.grey16,
    marginRight: 8,
    marginBottom: 6,
  },
  metaPillText: {
    fontSize: 11,
    color: Colors.grey1,
  },
  formContainer: {
    width: '100%',
    paddingHorizontal: 15,
    paddingBottom: 70,
  },
  addMoreButton: {
    borderWidth: 1,
    borderColor: Colors.blue0,
    borderRadius: 10,
    borderStyle: 'dashed',
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: Colors.white,
  },
  addMoreText: {
    color: Colors.blue0,
    fontSize: 14,
    marginBottom: 2,
  },
  addMoreSubtext: {
    fontSize: 11,
    color: Colors.grey1,
  },
});

export default React.memo(TodoCompetitorUpdate, (prev, next) => prev == next);
